import { paginate, type RestSchema } from "@mountaya/client";
import { z } from "zod";

import { subscriptionDiscountSchema } from "../../billing/rules/queries";
import type { RestClientConfig } from "../../rest/rules/sdk";
import type { components } from "../../schema/api";
import { organizationRoleSchema, organizationStatusSchema } from "./roles";
import {
  accountsFor,
  invitationsFor,
  membersFor,
  organizationsFor,
  publishableKeysFor,
  secretKeysFor,
} from "./sdk";

/**
 * organizationDefaultPropsSchema is the slice of the backend
 * EmbeddedStudioDefaultProps the Settings page renders and edits: the display
 * preferences (language/theme/length/pace/slope/temperature, mirrored under their wire
 * `preferred_*` names), the terrain selection, the overlay/terrain id lists, and
 * the `with_*` feature toggles. The non-strict parse drops any wire field not
 * listed, and the optional/catch defaults keep an older or partial settings
 * payload from failing the parse.
 */
const organizationDefaultPropsSchema = z.object({
  preferred_language: z.string().optional(),
  preferred_theme: z.string().optional(),
  preferred_unit_length: z.string().optional(),
  preferred_unit_pace: z.string().optional(),
  preferred_unit_slope: z.string().optional(),
  preferred_unit_temperature: z.string().optional(),
  segments_color: z.string().optional(),
  terrain: z.string().optional(),
  terrain_variant: z.string().optional(),
  with_collection_itineraries: z.boolean().catch(false),
  with_collection_zones: z.boolean().catch(false),
  with_directions_control: z.boolean().catch(false),
  with_download_control: z.boolean().catch(false),
  with_navigation_control: z.boolean().catch(false),
  with_overlays: z.array(z.string()).catch([]),
  with_overlays_control: z.boolean().catch(false),
  with_preferences_control: z.boolean().catch(false),
  with_profile_analysis: z.boolean().catch(false),
  with_profile_characteristics: z.boolean().catch(false),
  with_profile_elevation: z.boolean().catch(false),
  with_profile_pace_control: z.boolean().catch(false),
  with_streetview_control: z.boolean().catch(false),
  with_terrains: z.array(z.string()).catch([]),
});

/**
 * organizationBoundarySchema is one named bounding region in the org's embed safety
 * rules: the display name and the flat [minLongitude, minLatitude, maxLongitude,
 * maxLatitude] box, which is the four-number order every bounding box on the platform
 * speaks. It mirrors the generated wire shape exactly rather than parsing loosely, so a
 * boundary read here is assignable straight back to the PATCH body: the Security page's
 * geofence editor seeds from this value and writes it back with no cast anywhere in
 * between.
 */
const organizationBoundarySchema = z.object({
  bbox: z.tuple([z.number(), z.number(), z.number(), z.number()]),
  name: z.string(),
});

/**
 * organizationBoundaryEntrySchema parses one boundaries entry into a DISCRIMINATED
 * result rather than a value-or-nothing. Boundaries are a security allow-list where the
 * empty list is the PERMISSIVE default, so a malformed entry must never simply vanish:
 * dropping it would leave the page showing a narrower geofence than the one in force,
 * and the next save would persist that widening. Keeping an `unreadable` marker lets the
 * page state that a restriction exists it cannot display, and refuse to save over it,
 * while every readable region still renders.
 */
const organizationBoundaryEntrySchema = z.unknown().transform((entry) => {
  const parsed = organizationBoundarySchema.safeParse(entry);

  return parsed.success
    ? ({ boundary: parsed.data, status: "readable" } as const)
    : ({ status: "unreadable" } as const);
});

/**
 * OrganizationBoundaryEntry is one parsed boundaries entry: a readable region, or the
 * marker for one the client could not read. Consumers narrow on `status`; readable
 * entries carry a boundary assignable straight back to the PATCH body, so the Security
 * page's geofence editor seeds from it and writes it back with no cast in between.
 */
export type OrganizationBoundaryEntry = z.infer<typeof organizationBoundaryEntrySchema>;

/**
 * UNREADABLE_BOUNDARIES is the fallback for a `boundaries` field that is not a list at
 * all. It is one unreadable marker rather than an empty list for the same reason a bad
 * entry is: an empty list reads as "no geographic restriction", which is the permissive
 * end of the allow-list and the one answer a failed parse must never give.
 */
const UNREADABLE_BOUNDARIES: OrganizationBoundaryEntry[] = [{ status: "unreadable" }];

/**
 * organizationSafetyRulesSchema is the slice of the backend
 * EmbeddedStudioSafetyRules the read-only Security page renders: the
 * session-token requirement, the zoom range, the allow-listed domains, the
 * permitted overlays, the named boundaries, and the embed-permission `with_*`
 * booleans. Optional/catch defaults keep a partial payload parseable.
 */
const organizationSafetyRulesSchema = z.object({
  boundaries: z.array(organizationBoundaryEntrySchema).catch(UNREADABLE_BOUNDARIES),
  domains: z.array(z.string()).catch([]),
  require_session_token: z.boolean().catch(false),
  with_collection_itineraries: z.boolean().catch(false),
  with_collection_zones: z.boolean().catch(false),
  with_overlays: z.array(z.string()).catch([]),
  with_overlays_control: z.boolean().catch(false),
  with_profile_analysis: z.boolean().catch(false),
  with_profile_elevation: z.boolean().catch(false),
  zoom_max: z.number().optional(),
  zoom_min: z.number().optional(),
});

/**
 * organizationThemeColorsSchema is the embedded-studio map theme the Settings
 * page edits: the 10-stop `ui` scale a primary color generates. The `ui` array is
 * parsed loosely (a plain string array) rather than a strict 10-tuple so an older
 * or partial payload still parses; the page treats the first 10 stops as the
 * scale.
 */
const organizationThemeColorsSchema = z.object({
  ui: z.array(z.string()).catch([]),
});

/**
 * organizationThemeSchema is the wire `theme` block the Settings page seeds its
 * color inputs from. `colors` is optional so an organization with no theme set
 * still parses to an empty theme.
 */
const organizationThemeSchema = z.object({
  colors: organizationThemeColorsSchema.optional(),
});

/**
 * OrganizationThemeColors is the api-layer view of the embedded-studio theme
 * colors, derived from organizationThemeColorsSchema. The Settings page reads it
 * to seed the primary color input.
 */
export type OrganizationThemeColors = z.infer<typeof organizationThemeColorsSchema>;

/**
 * organizationSettingsSchema is the slice of the backend OrganizationSettings the
 * Settings + Security pages need: `default_props`, `theme`, and `safety_rules`, and the
 * settings' own `etag`, which a settings write sends back as its `ifMatch` so a write made over
 * somebody else's is refused rather than silently undoing it. The settings carry a version of
 * their own, apart from the organization's, so a rename and a settings edit never refuse each
 * other. Each sub-object is optional so an organization missing a settings section still
 * parses. The per-minute rate limits are not part of settings: they are entitlement limits
 * staff set (`entitlements.limits`), as every other limit of an agreement is.
 */
export const organizationSettingsSchema = z.object({
  default_props: organizationDefaultPropsSchema.optional(),
  etag: z.string(),
  safety_rules: organizationSafetyRulesSchema.optional(),
  theme: organizationThemeSchema.optional(),
});

/**
 * ConsoleOrganizationSettings is an organization's settings as the console parses them, their
 * `etag` with them: what a settings write is made on, and what it answers with.
 */
export type ConsoleOrganizationSettings = z.infer<typeof organizationSettingsSchema>;

/**
 * entitlementSchema mirrors the backend Capability enum: the products that are the
 * technical units of access. An organization's entitlements grant a subset of these, and
 * the billing page gates each feature on the matching capability. Zod-first so the
 * entitlement parse and the selector hooks share one definition.
 *
 * `data` is the one member bought self-serve, opened by a self-serve organization's first
 * card; every other member is agreed rather than bought, and granted by a staff entitlements
 * override. `studio-embedded` is what a publishable key needs to act as a member inside a
 * customer's own product; `white-label` gates no request at all, it is what takes the Mountaya
 * mark off an embedded map and an exported video.
 */
export const entitlementSchema = z.enum([
  "studio-online",
  "publishing",
  "tiles",
  "data",
  "studio-embedded",
  "white-label",
]);

/**
 * ProductCapability is one product an organization may hold, derived from entitlementSchema.
 *
 * Named for the product rather than bare `Capability` because the bus publishes a `Capability`
 * of its own (`@mountaya/elements/studio`), and the two vocabularies answer different questions:
 * this one is what an organization HOLDS, bought or agreed, while the bus's is what a studio
 * SURFACE may do. A file that needed both had two declarations of one word and no way to say
 * which it meant.
 */
export type ProductCapability = z.infer<typeof entitlementSchema>;

/**
 * EntitlementLimitKey is one numeric dimension an organization's agreement limits, and it is the
 * console's type: the agreement's limits are read on the account (`getOrganizationAccount`),
 * which the public contract does not publish.
 *
 * It is not the contract's `LimitKey`, and the two must not be confused, although both are
 * kebab-case. That one names the ceiling a `LIMIT_REACHED` refusal met (`seats`,
 * `published-itineraries`, `published-zones`, `personal-itineraries`, `pending-invitations`),
 * which includes ceilings no agreement sets; this one names the keys of `entitlements.limits`,
 * which include the request volumes no refusal of that code names. The two share `seats`,
 * `published-itineraries` and `published-zones`, spelled alike because they are the same
 * ceilings.
 *
 * The usage read beside the limits (`limit_usage`) is a different shape again: its fields stay
 * snake_case, so a key here is never a field name there.
 */
export type EntitlementLimitKey = components["schemas"]["EntitlementLimitKey"];

/**
 * entitlementLimitKeySchema mirrors the console's EntitlementLimitKey enum: the numeric
 * dimensions an organization's agreement limits. Staff set each one, and every one reads a
 * default otherwise: the seats (members and pending invitations), the itineraries and the zones
 * published at once, the Data API and Tile API requests of a calendar month, and the per-minute
 * rate limits of both. The entitlements an organization reads back always carry all seven keys, a
 * null value being unlimited. Zod-first so the entitlement parse and the useLimit selector share one
 * definition.
 */
export const entitlementLimitKeySchema = z.enum([
  "seats",
  "published-itineraries",
  "published-zones",
  "data-requests-per-month",
  "tiles-requests-per-month",
  "data-requests-per-minute",
  "tiles-requests-per-minute",
]) satisfies z.ZodType<EntitlementLimitKey>;

/**
 * SupportTier is the support commitment an organization holds, and it is the console's type: it
 * rides the account, which the public contract does not publish.
 *
 * Every `satisfies z.ZodType<…>` in this module is HALF the guarantee, for the reason
 * `roles.ts` spells out: a schema producing a NARROWER union still satisfies it, because a
 * narrower output is assignable to a wider one. It catches a member a schema invents that
 * the contract lacks; it does not catch one the contract gained and the schema lacks,
 * which is the direction that breaks, since a closed enum then rejects a real account
 * read at the parse. `queries.test.ts` holds that direction with a type-level equality per
 * enum.
 */
export type SupportTier = components["schemas"]["SupportTier"];

/**
 * supportTierSchema mirrors the backend SupportTier enum: the support commitment an
 * organization holds. It gates no request, it is what the console states the organization
 * may ask for, and it is always present, so an organization with no agreed tier reads
 * "none" rather than nothing and no client has to decide what an absent tier means.
 */
export const supportTierSchema = z.enum([
  "none",
  "business",
  "premium",
]) satisfies z.ZodType<SupportTier>;

/**
 * SubscriptionStatus is the Stripe subscription lifecycle status, and it is the contract's
 * type.
 */
export type SubscriptionStatus = RestSchema.SubscriptionStatus;

/**
 * subscriptionStatusSchema mirrors the backend SubscriptionStatus enum: the
 * Stripe subscription lifecycle the billing page branches its state matrix on.
 */
export const subscriptionStatusSchema = z.enum([
  "active",
  "trialing",
  "past-due",
  "canceled",
  "unpaid",
  "incomplete",
  "incomplete-expired",
  "paused",
]) satisfies z.ZodType<SubscriptionStatus>;

/**
 * EntitlementsSource is what an organization's entitlements follow from, and it is the console's
 * type, read on the account and on the staff search.
 */
export type EntitlementsSource = components["schemas"]["EntitlementsSource"];

/**
 * entitlementsSourceSchema mirrors the backend EntitlementsSource enum: what an
 * organization's entitlements follow from, and therefore how it is billed. `custom` is an
 * organization billed by agreement, holding exactly what staff forced on; `subscription` is a
 * self-serve one, holding what its Data API subscription grants, and only it is asked for a
 * card on file.
 *
 * It is a closed enum rather than a free string for the reason `support_tier` below is: the
 * set is closed on the wire, and a source the console cannot name is one it cannot decide
 * the card-on-file question from. The capability MAP beside it stays open, and the asymmetry
 * is deliberate, an unknown map KEY is simply a dimension nothing asks about, where an
 * unknown source is an answer to a question the page does ask.
 */
export const entitlementsSourceSchema = z.enum([
  "subscription",
  "custom",
]) satisfies z.ZodType<EntitlementsSource>;

/**
 * OrganizationEntitlements is an organization's read-only authorization truth, and it is the
 * console's type: the schema below parses every field the account declares, so the alias claims
 * nothing the parse drops.
 */
export type OrganizationEntitlements = components["schemas"]["OrganizationEntitlements"];

/**
 * entitlementLimitsSchema is the limits an organization is held to: the closed set of seven
 * EntitlementLimitKey dimensions, every one always sent. A value is nullable and a null means
 * the dimension is uncapped: the backend keeps its unlimited sentinel internal and spells
 * unlimited as a null on the wire, so no magic number crosses the boundary. A count of 0 is a
 * real limit and allows nothing. Every key is required, so a read that leaves one out fails
 * the parse rather than reaching a meter as a dimension nobody stated; the non-strict parse
 * drops a key the contract has not named yet.
 */
const entitlementLimitsSchema = z.object({
  "data-requests-per-minute": z.number().nullable(),
  "data-requests-per-month": z.number().nullable(),
  "published-itineraries": z.number().nullable(),
  "published-zones": z.number().nullable(),
  seats: z.number().nullable(),
  "tiles-requests-per-minute": z.number().nullable(),
  "tiles-requests-per-month": z.number().nullable(),
}) satisfies z.ZodType<OrganizationEntitlements["limits"]>;

/**
 * organizationEntitlementsSchema is the organization's read-only authorization
 * truth the billing page gates on: where the entitlements were derived from, the
 * granted capabilities keyed by capability slug, and the limits above. The
 * capability map is parsed as an open record keyed by string rather than the enum:
 * the backend may add a capability before the frontend enum does, and a
 * forward-compatible map keeps an unknown key from failing the parse (the selector
 * hooks read by the known enum keys). `support_tier` is parsed through its
 * closed enum instead, because it is always present and has exactly one least value: a
 * tier the frontend does not know is a tier it cannot render, where an unknown capability
 * is simply one nothing asks about. Entitlements are server state, so they ride the account
 * query cache, never the identity store.
 */
const organizationEntitlementsSchema = z.object({
  capabilities: z.record(z.string(), z.boolean()),
  limits: entitlementLimitsSchema,
  source: entitlementsSourceSchema,
  support_tier: supportTierSchema,
}) satisfies z.ZodType<OrganizationEntitlements>;

/**
 * meterDimensionSchema mirrors the backend MeterDimension enum: which metered surface a
 * subscription line bills. A line that bills a flat price carries none, so the field it
 * sits on is optional.
 */
export const meterDimensionSchema = z.enum([
  "tiles",
  "data",
]) satisfies z.ZodType<RestSchema.MeterDimension>;

/**
 * subscriptionCollectionMethodSchema mirrors the backend SubscriptionCollectionMethod
 * enum: how Stripe collects on a subscription. It is what says whether a card on file
 * changes anything, one billed by invoice is settled outside it.
 */
export const subscriptionCollectionMethodSchema = z.enum([
  "charge-automatically",
  "send-invoice",
]) satisfies z.ZodType<RestSchema.SubscriptionCollectionMethod>;

/**
 * organizationSubscriptionItemSchema is one line item of the organization's Stripe
 * subscription: the Stripe price id, the quantity, and the metered dimension it bills. The
 * Data API subscription carries one metered line, billed by the month on the requests the
 * month reported, so a line states no interval of its own.
 */
const organizationSubscriptionItemSchema = z.object({
  meter: meterDimensionSchema.optional(),
  price_id: z.string(),
  quantity: z.number(),
});

/**
 * OrganizationSubscription is one of an organization's Stripe subscriptions, and it is the
 * contract's type: the schema below parses every field the contract declares, so the alias
 * claims nothing the parse drops.
 */
export type OrganizationSubscription = RestSchema.OrganizationSubscription;

/**
 * organizationSubscriptionSchema is the read-only summary of the organization's
 * Stripe subscription the billing page renders: the lifecycle status, how Stripe
 * collects payment, the billing currency, the line items, when the current period
 * ends, whether it is set to cancel at period end, whether billing on it is paused, and
 * the active promo discount (absent when none is applied). `current_period_end_at` is
 * `z.date()` because the transport revives the wire date-time string before this parses.
 * The non-strict parse drops any wire field the page does not read.
 *
 * `paused` is read even though `status` already carries a `paused` member, because the two
 * describe different mechanisms and only one of them moves the status: a subscription whose
 * collection an operator paused reports `active`. The backend answers the question a reader
 * actually has, is this collecting anything, in this one field, so a surface never has to
 * know there were two ways to ask.
 */
export const organizationSubscriptionSchema = z.object({
  cancel_at_period_end: z.boolean(),
  collection_method: subscriptionCollectionMethodSchema,
  currency: z.string(),
  current_period_end_at: z.date(),
  discount: subscriptionDiscountSchema.optional(),
  items: z.array(organizationSubscriptionItemSchema),
  paused: z.boolean(),
  status: subscriptionStatusSchema,
}) satisfies z.ZodType<OrganizationSubscription>;

/**
 * OrganizationSubscriptionSummary is an org's subscriptions split by kind, and it is the
 * contract's type.
 */
export type OrganizationSubscriptionSummary = RestSchema.OrganizationSubscriptionSummary;

/**
 * organizationSubscriptionSummarySchema is the org's billing subscription: the metered Data
 * API subscription, the one subscription an organization holds self-serve, opened by its first
 * saved card, and whether a default payment method is on file. The slot is optional, absent when
 * the org has never held that subscription. What an agreement grants (Map Studio, Map
 * Publishing, the Tile API) is no subscription and is never here. It reuses
 * organizationSubscriptionSchema so the subscription parses and revives as the single read
 * does. It lives here (next to the single-subscription schema it composes) so both the
 * account read-model (organizationAccountSchema below) and the dedicated billing endpoint share
 * one definition without a circular import.
 *
 * The slot is ABSENT and never null. The nullable answer belongs one level up, the
 * dedicated billing endpoint answers `data: null` for an org that holds no subscription at
 * all, so accepting a null here would let a shape the wire cannot produce through the
 * boundary and type it as one it can.
 */
export const organizationSubscriptionSummarySchema = z.object({
  api: organizationSubscriptionSchema.optional(),
  has_payment_method: z.boolean().optional(),
}) satisfies z.ZodType<OrganizationSubscriptionSummary>;

/**
 * OrganizationLimitUsage is how much of each counted limit an organization uses, and it is the
 * console's type, read on the account.
 */
export type OrganizationLimitUsage = components["schemas"]["OrganizationLimitUsage"];

/**
 * organizationLimitUsageSchema is how much of each counted limit an organization uses, read
 * the way the limits are enforced: the seats taken (pending invitations included), the
 * itineraries and the zones published now, and the Data API and Tile API requests of the current
 * calendar month in UTC. The per-minute limits have no usage.
 */
export const organizationLimitUsageSchema = z.object({
  data_requests_per_month: z.number(),
  published_itineraries: z.number(),
  published_zones: z.number(),
  seats: z.number(),
  tiles_requests_per_month: z.number(),
}) satisfies z.ZodType<OrganizationLimitUsage>;

/**
 * organizationSchema is the narrow slice of the contract's Organization the IAM pages render:
 * id, name, the creation timestamp, the `etag` a profile write sends back as its `ifMatch`,
 * whether its creator asked for an agreement that has not been set yet (`awaiting_agreement`),
 * whether it may take the Mountaya mark off its embeds (`white_label`), and the settings slice
 * above. Zod-first: ConsoleOrganization (the console's projection) derives from it. `created_at`
 * is `z.date()` because the transport revives the wire date-time string into a real Date before
 * this parses. `awaiting_agreement` and `white_label` read false where a read leaves them out or
 * sends something else, the readings that ask nothing of anybody and paint the mark.
 *
 * What the organization's account holds (its entitlements, the usage of each counted limit, its
 * subscription summary, whether the user may change billing, its lifecycle status) is no longer
 * on this read: it is the console's {@link organizationAccountSchema}, read on its own. The
 * organization read is answered to every credential that may see the organization, a share link
 * and a publishable key included, and those must not learn the account.
 *
 * The shape it yields is {@link ConsoleOrganization} and not the contract's `Organization`,
 * and the two are different types on purpose, see the type below.
 */
export const organizationSchema = z.object({
  awaiting_agreement: z.boolean().catch(false),
  created_at: z.date(),
  etag: z.string(),
  id: z.string(),
  name: z.string(),
  settings: organizationSettingsSchema,
  white_label: z.boolean().catch(false),
});

/**
 * ConsoleOrganization is the organization as the console parses it, and it is NOT the
 * contract's `Organization`.
 *
 * The schema above is a deliberate slice: it drops the required `updated_at`, and re-shapes
 * `settings` into the sub-slices the Settings and Security pages edit rather than the whole
 * `OrganizationSettings`. Aliasing the contract's type over it would claim every one of those on
 * a value that never carried them, and a call site reading `organization.updated_at` would
 * compile against a field the parse discarded. The distinct name is what keeps the console's
 * projection and the wire resource distinguishable at every call site.
 */
export type ConsoleOrganization = z.infer<typeof organizationSchema>;

/**
 * OrganizationAccount is the account behind an organization, and it is the console's type: the
 * schema below parses every field `getOrganizationAccount` declares, so the alias claims nothing
 * the parse drops.
 */
export type OrganizationAccount = components["schemas"]["OrganizationAccount"];

/**
 * organizationAccountSchema is the account behind an organization, the console's own read of
 * what the public organization leaves off: whether the organization is active, the entitlements
 * its agreement grants, how much of each counted limit is in use (absent when a count could not
 * be read at that moment), its Data API subscription summary (absent when it holds none), and
 * whether the signed-in user may change its billing. Only a console session reads it, a member's
 * of any role or a Mountaya staff member's, who reads `can_manage_billing` false.
 */
export const organizationAccountSchema = z.object({
  can_manage_billing: z.boolean(),
  entitlements: organizationEntitlementsSchema,
  limit_usage: organizationLimitUsageSchema.optional(),
  organization_id: z.string(),
  status: organizationStatusSchema,
  subscription: organizationSubscriptionSummarySchema.optional(),
}) satisfies z.ZodType<OrganizationAccount>;

/**
 * OrganizationMember is one member of an organization, and it is the contract's type: the
 * schema below parses every field the contract declares, so the alias claims nothing the
 * parse drops.
 */
export type OrganizationMember = RestSchema.OrganizationMember;

/**
 * organizationMemberSchema is the contract's OrganizationMember as the members page renders
 * it: the user id (the role, remove and transfer writes name it), the account email, the
 * display name, the role, and when the member joined. Every member listed has joined: an
 * invitation still waiting for an answer is no membership and is listed by
 * {@link fetchOrganizationInvitations} instead. `name` is optional because it is populated per
 * account and may be absent; a member with no name falls back to the email at the call site.
 * `email` is optional too: the listing omits it for a member whose account cannot be resolved,
 * and such a member is named by neither. The non-strict parse drops any extra wire field.
 */
export const organizationMemberSchema = z.object({
  email: z.string().optional(),
  joined_at: z.date(),
  name: z.string().optional(),
  role: organizationRoleSchema,
  user_id: z.string(),
}) satisfies z.ZodType<OrganizationMember>;

/**
 * OrganizationInvitation is one invitation to an organization still waiting for an answer, and
 * it is the contract's type.
 */
export type OrganizationInvitation = RestSchema.OrganizationInvitation;

/**
 * assignableRoleSchema parses the role an invitation grants: every role but `owner`, which is
 * never granted by invitation.
 */
const assignableRoleSchema = z.enum([
  "admin",
  "contributor",
  "viewer",
]) satisfies z.ZodType<RestSchema.AssignableOrganizationRole>;

/**
 * organizationInvitationSchema is one pending invitation as the members page lists it: the
 * address invited (trimmed and lowercased as stored), the role accepting grants, who made it,
 * when, and when it lapses unanswered. It names an address and never an account: whether an
 * account carries the address is not part of the answer.
 */
export const organizationInvitationSchema = z.object({
  created_at: z.date(),
  email: z.string(),
  expires_at: z.date(),
  id: z.string(),
  invited_by: z.object({ id: z.string() }),
  role: assignableRoleSchema,
}) satisfies z.ZodType<OrganizationInvitation>;

/**
 * PublishableKeyScope is one scope a publishable key carries, and it is the contract's type.
 */
export type PublishableKeyScope = RestSchema.PublishableKeyScope;

/**
 * publishableKeyScopeSchema mirrors the contract's PublishableKeyScope enum, in the order
 * the contract declares it. Each scope names one product surface (Map Publishing, Embedded
 * Studio, the Tile API, the Data API, the MCP server) except `management`, which names the
 * organization's own account. A key carries exactly the scopes it was given and none is
 * added because another is present. The one relation between them is an inclusion, not an
 * addition: an operation that asks for `publishing` also accepts a key carrying
 * `studio-embedded`.
 */
export const publishableKeyScopeSchema = z.enum([
  "publishing",
  "studio-embedded",
  "tiles",
  "data",
  "mcp",
  "management",
]) satisfies z.ZodType<PublishableKeyScope>;

/**
 * publishableKeySchema is the narrow slice of the backend PublishableKey the API
 * keys page renders. The date fields ride the transport's revival (so they parse
 * as Date), and `last_used_at`/`revoked_at` are optional — a key that has never
 * been used and is still active carries neither. `etag` is what an edit or a revoke sends back
 * as its `ifMatch`, so one made over a change somebody else made is refused rather than
 * undoing it. The non-strict parse drops the organization id the page does not show.
 */
export const publishableKeySchema = z.object({
  created_at: z.date(),
  etag: z.string(),
  id: z.string(),
  key: z.string(),
  last_used_at: z.date().optional(),
  name: z.string(),
  revoked_at: z.date().optional(),
  scopes: z.array(publishableKeyScopeSchema),
});

/**
 * ConsolePublishableKey is a publishable key as the console parses it, and it is NOT the
 * contract's `PublishableKey`.
 *
 * The slice above drops the required `organization_id`: a key is only ever read through
 * the organization that owns it, so the page has the id already and never shows it back.
 * Aliasing the contract's type would claim that field on a value the parse discarded, for
 * the same reason {@link ConsoleOrganization} carries its own name.
 */
export type ConsolePublishableKey = z.infer<typeof publishableKeySchema>;

/**
 * secretKeySchema is the narrow slice of the backend SecretKey the API keys page
 * renders. `key` is optional: the backend only returns the secret value at
 * creation time, so a listed key carries no `key`. The date fields ride the
 * revival, and `last_used_at`/`revoked_at` are optional for the same reason as
 * publishable keys. `created_by` is the user who created the key, which the page shows beside
 * it: a secret key mints sessions naming any member, so who made one is worth knowing after
 * they have left. It is null for a key created before that was recorded. `suffix` is the key
 * value's last four characters, answered on every read: the list shows it so a member holding a
 * key can tell which listed key it is, and which one to revoke, without the value being stored.
 */
export const secretKeySchema = z.object({
  created_at: z.date(),
  created_by: z.string().nullable(),
  id: z.string(),
  key: z.string().optional(),
  last_used_at: z.date().optional(),
  name: z.string(),
  revoked_at: z.date().optional(),
  suffix: z.string(),
});

/**
 * SecretKey is the api-layer view of a secret key, derived from secretKeySchema.
 */
export type SecretKey = z.infer<typeof secretKeySchema>;

/**
 * organizationResponseSchema is the REST envelope around a single organization.
 */
const organizationResponseSchema = z.object({ data: organizationSchema });

/**
 * organizationAccountResponseSchema is the REST envelope around an organization's account.
 */
const organizationAccountResponseSchema = z.object({ data: organizationAccountSchema });

/**
 * organizationQueryKey is the TanStack Query key for a single organization read.
 * The convention across the IAM reads is `["organizations", id, "<url-segment>"]`;
 * the bare key here is the organization root, so invalidating it without `exact` reaches every
 * read under the organization. Disjoint from the publishable-key lookup key
 * (`["organizations", "lookup", pk]`) since the id is never the literal "lookup".
 */
export const organizationQueryKey = (organizationId: string) =>
  ["organizations", organizationId] as const;

/**
 * organizationAccountQueryKey is the TanStack Query key for an organization's account. A write
 * that changes only what the account counts (an invitation holding a seat, a member removed, a
 * billing write) invalidates this key alone rather than the organization root.
 */
export const organizationAccountQueryKey = (organizationId: string) =>
  ["organizations", organizationId, "account"] as const;

/**
 * organizationMembersQueryKey is the TanStack Query key for an organization's
 * member list.
 */
export const organizationMembersQueryKey = (organizationId: string) =>
  ["organizations", organizationId, "members"] as const;

/**
 * organizationInvitationsQueryKey is the TanStack Query key for the invitations to an
 * organization still waiting for an answer.
 */
export const organizationInvitationsQueryKey = (organizationId: string) =>
  ["organizations", organizationId, "invitations"] as const;

/**
 * publishableKeysQueryKey is the TanStack Query key for an organization's
 * publishable keys.
 */
export const publishableKeysQueryKey = (organizationId: string) =>
  ["organizations", organizationId, "publishable-keys"] as const;

/**
 * secretKeysQueryKey is the TanStack Query key for an organization's secret keys.
 */
export const secretKeysQueryKey = (organizationId: string) =>
  ["organizations", organizationId, "secret-keys"] as const;

/**
 * fetchOrganization is a hookless query-fn reading one organization by id. The client
 * projects the config's auth strategy onto the request (the session default sends the
 * Ory cookie) and Zod-parses the envelope's data at the boundary, so the page consumes
 * the narrow, validated slice. A non-ok response throws a typed error so TanStack Query
 * drives its error state.
 */
export const fetchOrganization = async (
  config: RestClientConfig,
  organizationId: string,
): Promise<ConsoleOrganization> => {
  const envelope = await organizationsFor(config).getOrganization({
    organization_id: organizationId,
  });

  return organizationResponseSchema.parse(envelope).data;
};

/**
 * fetchOrganizationAccount is a hookless query-fn reading the account behind an organization
 * through the console client: `getOrganizationAccount` is answered to a console session alone, of
 * a member in any role or of a Mountaya staff member. Same parse and throw contract as
 * fetchOrganization.
 */
export const fetchOrganizationAccount = async (
  config: RestClientConfig,
  organizationId: string,
): Promise<OrganizationAccount> => {
  const envelope = await accountsFor(config).getOrganizationAccount({
    organization_id: organizationId,
  });

  return organizationAccountResponseSchema.parse(envelope).data;
};

/**
 * OrganizationSettingsDocument is an organization's settings exactly as the API reads them back:
 * the default props, the theme and the safety rules, every field present and nothing parsed
 * away. It is the contract's type.
 */
export type OrganizationSettingsDocument = RestSchema.OrganizationSettings;

/**
 * fetchOrganizationSettings reads an organization's settings document as the wire carries it,
 * with no parse in between. It is the read a whole-document write starts from: a replace sends
 * back every field it did not change, so it has to be handed the stored values themselves and
 * not the console's projection of them, which reshapes the boundaries and fills defaults in.
 * Same auth/throw contract as fetchOrganization; a staff console session reads any customer
 * organization's settings through the membership waiver.
 */
export const fetchOrganizationSettings = async (
  config: RestClientConfig,
  organizationId: string,
): Promise<OrganizationSettingsDocument> => {
  const envelope = await organizationsFor(config).getOrganizationSettings({
    organization_id: organizationId,
  });

  return envelope.data;
};

/**
 * fetchOrganizationMembers is a hookless query-fn reading an organization's
 * member list, whole. Same auth/throw contract as fetchOrganization.
 *
 * The listing is served a page at a time, and a caller asking who is in an organization means
 * everyone. The SDK's `paginate` is the walk: it sends `limit` and the `cursor` the previous page
 * named under `query`, where the operation reads them, and follows `next_cursor` until it reads
 * null. Each row is Zod-parsed as the walk answers it, so the page consumes the narrow, validated
 * slice.
 */
export const fetchOrganizationMembers = async (
  config: RestClientConfig,
  organizationId: string,
): Promise<OrganizationMember[]> => {
  const members: OrganizationMember[] = [];

  for await (const row of paginate(membersFor(config).getOrganizationMembers, {
    organization_id: organizationId,
  })) {
    members.push(organizationMemberSchema.parse(row));
  }

  return members;
};

/**
 * fetchOrganizationInvitations is a hookless query-fn reading the invitations to an organization
 * still waiting for an answer, whole: the same cursor walk and parse as fetchOrganizationMembers.
 * Every invitation listed holds a seat; one accepted, declined, withdrawn or expired leaves the
 * listing.
 */
export const fetchOrganizationInvitations = async (
  config: RestClientConfig,
  organizationId: string,
): Promise<OrganizationInvitation[]> => {
  const invitations: OrganizationInvitation[] = [];

  for await (const row of paginate(invitationsFor(config).getOrganizationInvitations, {
    organization_id: organizationId,
  })) {
    invitations.push(organizationInvitationSchema.parse(row));
  }

  return invitations;
};

/**
 * fetchPublishableKeys is a hookless query-fn reading an organization's
 * publishable keys, whole. Same walk and auth/parse/throw contract as
 * fetchOrganizationMembers.
 */
export const fetchPublishableKeys = async (
  config: RestClientConfig,
  organizationId: string,
): Promise<ConsolePublishableKey[]> => {
  const keys: ConsolePublishableKey[] = [];

  for await (const row of paginate(publishableKeysFor(config).getOrganizationPublishableKeys, {
    organization_id: organizationId,
  })) {
    keys.push(publishableKeySchema.parse(row));
  }

  return keys;
};

/**
 * fetchSecretKeys is a hookless query-fn reading an organization's secret keys,
 * whole. Listed keys never carry the secret value (it is shown once at creation),
 * so each parsed key's `key` field is absent. Same walk and auth/parse/throw
 * contract as fetchOrganizationMembers: the console's own operation takes its
 * cursor under `query` and answers the page shape a published listing does, so
 * `paginate` walks it over the same transport.
 */
export const fetchSecretKeys = async (
  config: RestClientConfig,
  organizationId: string,
): Promise<SecretKey[]> => {
  const keys: SecretKey[] = [];

  for await (const row of paginate(secretKeysFor(config).getOrganizationSecretKeys, {
    organization_id: organizationId,
  })) {
    keys.push(secretKeySchema.parse(row));
  }

  return keys;
};

/**
 * organizationQueryOptions bundles the query key + hookless fetch fn so the
 * useQuery hook and any render-free caller share one cache entry.
 */
export const organizationQueryOptions = (config: RestClientConfig, organizationId: string) => ({
  queryFn: () => fetchOrganization(config, organizationId),
  queryKey: organizationQueryKey(organizationId),
});

/**
 * organizationAccountQueryOptions bundles the account query key + fetch fn.
 */
export const organizationAccountQueryOptions = (
  config: RestClientConfig,
  organizationId: string,
) => ({
  queryFn: () => fetchOrganizationAccount(config, organizationId),
  queryKey: organizationAccountQueryKey(organizationId),
});

/**
 * organizationMembersQueryOptions bundles the member-list query key + fetch fn.
 */
export const organizationMembersQueryOptions = (
  config: RestClientConfig,
  organizationId: string,
) => ({
  queryFn: () => fetchOrganizationMembers(config, organizationId),
  queryKey: organizationMembersQueryKey(organizationId),
});

/**
 * organizationInvitationsQueryOptions bundles the pending-invitations query key + fetch fn.
 */
export const organizationInvitationsQueryOptions = (
  config: RestClientConfig,
  organizationId: string,
) => ({
  queryFn: () => fetchOrganizationInvitations(config, organizationId),
  queryKey: organizationInvitationsQueryKey(organizationId),
});

/**
 * publishableKeysQueryOptions bundles the publishable-key query key + fetch fn.
 */
export const publishableKeysQueryOptions = (config: RestClientConfig, organizationId: string) => ({
  queryFn: () => fetchPublishableKeys(config, organizationId),
  queryKey: publishableKeysQueryKey(organizationId),
});

/**
 * secretKeysQueryOptions bundles the secret-key query key + fetch fn.
 */
export const secretKeysQueryOptions = (config: RestClientConfig, organizationId: string) => ({
  queryFn: () => fetchSecretKeys(config, organizationId),
  queryKey: secretKeysQueryKey(organizationId),
});
